import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SearchResult } from '@ukam/core/searchs/search-result';
import { API_BASE_URL } from '@ukam/core/tokens/api-base-url.token';
import { StoredFolder, StoredFolderMoveParams, StoredFolderParams } from '../models/stored-folder';

export interface StoredFolderListQuery {
  /** Storage (disco). */
  storageId?: string | null;
  /** Ambos opcionais e independentes; ausentes = pastas sem esse vínculo. */
  feature?: string | null;
  parentNid?: number | null;
  /** Nulo = raiz do dono. Ignorado quando tree = true. */
  folderId?: string | null;
  /** Árvore inteira do dono em pré-ordem (depth, name). */
  tree?: boolean;
  /** Paginação: sem limit a consulta vem inteira (usada pela árvore). */
  offset?: number;
  limit?: number;
}

@Injectable({ providedIn: 'root' })
export class StoredFolderApiService {
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly http = inject(HttpClient);
  private readonly url = `${this.apiBaseUrl}/folders`;

  /** Consulta paginada no padrão do sistema (POST /folders/search com StoredFolderFilter). */
  search(filter: Record<string, unknown>): Observable<SearchResult<StoredFolder>> {
    return this.http.post<SearchResult<StoredFolder>>(`${this.url}/search`, filter);
  }

  /** Filhas diretas de uma pasta (ou da raiz) ou a árvore inteira (tree). Sem limit, vem tudo. */
  list(query: StoredFolderListQuery): Observable<StoredFolder[]> {
    return this.listPage(query).pipe(map(r => r?.items ?? []));
  }

  /** Mesma consulta do list, devolvendo o resultado paginado (count/offset/limit/pages). */
  listPage(query: StoredFolderListQuery): Observable<SearchResult<StoredFolder>> {
    const paged = (query.limit ?? 0) > 0;
    const filter: Record<string, unknown> = paged
      ? { offset: query.offset ?? 0, limit: query.limit }
      : { unpaged: true, limit: 0 };
    if (query.storageId) filter['storage'] = { id: { eq: query.storageId } };
    if (query.feature) filter['feature'] = { key: { eq: query.feature } };
    if ((query.parentNid ?? 0) > 0) filter['parentNid'] = { eq: query.parentNid };
    if (!query.tree) {
      if (query.folderId) filter['folderId'] = { eq: query.folderId };
      else filter['rootOnly'] = true;
    }
    return this.search(filter);
  }

  getById(id: string): Observable<StoredFolder> {
    return this.http.get<StoredFolder>(`${this.url}/${id}`);
  }

  create(params: StoredFolderParams): Observable<StoredFolder> {
    return this.http.post<StoredFolder>(this.url, params);
  }

  update(id: string, params: StoredFolderParams): Observable<StoredFolder> {
    return this.http.put<StoredFolder>(`${this.url}/${id}`, params);
  }

  move(id: string, params: StoredFolderMoveParams): Observable<StoredFolder> {
    return this.http.post<StoredFolder>(`${this.url}/${id}/move`, params);
  }

  delete(id: string): Observable<unknown> {
    return this.http.delete(`${this.url}/${id}`);
  }
}
